import React from 'react';
import classNames from 'classnames';
import useImmutableState from '@betgames/bg-tools/dist/hooks/useImmutableState';
import anime, { AnimeTimelineInstance } from 'animejs';
import { AnimeEasingOptions } from 'twain-sport-fe-mono/enums';
import classes from './ParticipantScore.module.scss';

const SHOW_NEW_SCORE_TIME = 700; // 0.7s
const FADE_IN_OUT_DURATION = 200; // 0.2s
const FADE_IN_SCORE_DURATION = 300; // 0.3s

interface IProps {
    value: number;
    className?: string;
    withoutAnimation?: boolean;
}

export const ParticipantScore: React.FC<IProps> = (props) => {
    const scoreRef = React.useRef<HTMLDivElement>(null);
    const differenceRef = React.useRef<HTMLDivElement>(null);

    const [state, updateState] = useImmutableState({
        score: props.value,
        difference: 0,
    });

    React.useEffect(() => {
        let timeline: AnimeTimelineInstance;
        const difference = props.value - state.score;
        if (props.withoutAnimation) {
            return undefined;
        }
        if (difference) {
            updateState((draft) => {
                draft.difference = difference;
            });

            if (timeline && !timeline.completed) {
                timeline.pause();
            }
            timeline = anime
                .timeline({
                    duration: FADE_IN_OUT_DURATION,
                    easing: AnimeEasingOptions.EASE_IN_OUT_EXPO,
                    loop: false,
                })
                // hide score
                .add({
                    targets: scoreRef.current,
                    opacity: [1, 0],
                })
                // show and animate difference
                .add(
                    {
                        targets: differenceRef.current,
                        scale: [0.2, 1],
                        opacity: [0, 1],
                        duration: FADE_IN_SCORE_DURATION,
                        complete: () => {
                            updateState((draft) => {
                                draft.score = props.value;
                            });
                        },
                    },
                    '-=200',
                )
                // hide difference and show total score after SHOW_NEW_SCORE_TIME
                .add(
                    {
                        targets: differenceRef.current,
                        opacity: 0,
                    },
                    `+=${SHOW_NEW_SCORE_TIME}`,
                )
                .add(
                    {
                        targets: scoreRef.current,
                        opacity: [0, 1],
                    },
                    '-=200',
                );
        }

        return () => {
            if (timeline && !timeline.completed) {
                timeline.pause();
            }
        };
    }, [props.value]);

    return (
        <div className={classNames(classes.container, props.className)}>
            <div className={classes.score} ref={scoreRef}>
                {props.withoutAnimation ? props.value : state.score}
            </div>
            {!props.withoutAnimation && (
                <div className={classes.difference} ref={differenceRef}>
                    +{state.difference}
                </div>
            )}
        </div>
    );
};
